import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowLeft, Sparkle, Package, Users, TrendUp, Wallet, Warning } from "@phosphor-icons/react";
import api, { fmtGNF, logError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PERIODS = [["7d", "7 jours"], ["30d", "30 jours"], ["month", "Mois"]];
const LINES = [
  { key: "revenue", name: "Chiffre d'affaires", color: "#2E7D5B" },
  { key: "profit", name: "Bénéfice net", color: "#16233D" },
  { key: "expenses", name: "Dépenses / charges", color: "#B4432F" },
];

function SectionTitle({ icon: Icon, children, testid }) {
  return (
    <div className="flex items-center gap-2 pt-2" data-testid={testid}>
      <span className="w-8 h-8 rounded-xl bg-[#16233D] text-white flex items-center justify-center shrink-0"><Icon size={16} /></span>
      <h2 className="font-display font-bold text-lg md:text-lg">{children}</h2>
      <span className="flex-1 h-px bg-[#E9E4DA]" />
    </div>
  );
}

const CHART_MARGIN = { top: 5, right: 10, left: 0, bottom: 0 };
const AXIS_TICK = { fontSize: 11 };
const LEGEND_STYLE = { fontSize: 12 };
const LINE_DOT = { r: 2.5 };
const CHART_BOX = { width: "100%", height: 280, minHeight: 280 };

function ComboChart({ series }) {
  if (!series || series.length === 0) return <p className="text-sm text-gray-400 py-8">Aucun mouvement sur la période.</p>;
  return (
    <div style={CHART_BOX}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={CHART_MARGIN}>
          <CartesianGrid stroke="#E9E4DA" vertical={false} />
          <XAxis dataKey="day" tick={AXIS_TICK} tickFormatter={(d) => d.slice(5)} />
          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={48} />
          <Tooltip formatter={(v) => fmtGNF(v)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          {LINES.map((l) => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2.5} dot={LINE_DOT} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function StockSection({ stock }) {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="kcard p-5" data-testid="details-stock-summary">
        <p className="text-xs text-gray-500">Produits suivis</p>
        <p className="font-display font-bold text-2xl tabular-nums">{stock.products_count}</p>
        <p className="text-xs text-gray-500 mt-3">Valeur du stock (prix d'achat)</p>
        <p className="font-display font-bold tabular-nums">{fmtGNF(stock.stock_value)}</p>
      </div>
      <div className="kcard p-5 lg:col-span-2" data-testid="details-stock-risk">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2">
          <Warning size={17} color="#B4432F" /> Produits à risque
          <span className="text-xs font-normal text-gray-500">({stock.at_risk_count})</span>
        </h3>
        {stock.at_risk.length === 0 ? <p className="text-sm text-gray-400 py-4">Aucun produit sous son seuil d'alerte.</p> : (
          <div className="space-y-1.5">
            {stock.at_risk.map((p) => (
              <div key={p.id} data-testid="stock-risk-row" className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-sm">
                <span className="flex-1 min-w-0 truncate font-semibold">{p.name}</span>
                <span className="text-xs text-gray-500 tabular-nums">seuil {p.alert_threshold}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.level === "rupture" ? "badge-unpaid" : "badge-partial"}`}>
                  {p.level === "rupture" ? "Rupture" : `${p.stock_qty} restants`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FinanceSection({ finances }) {
  const max = Math.max(1, ...finances.expense_breakdown.map((x) => x.amount));
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="kcard p-5" data-testid="details-finance-kpis">
        <h3 className="font-display font-bold mb-3">Marge & tendances</h3>
        <div className="grid grid-cols-2 gap-3">
          {[["Chiffre d'affaires", fmtGNF(finances.revenue)], ["Dépenses", fmtGNF(finances.total_expenses)],
            ["Bénéfice net", fmtGNF(finances.profit)], ["Marge", `${finances.margin_pct} %`],
            ["Créances clients", fmtGNF(finances.receivables)], ["Dettes fournisseurs", fmtGNF(finances.payables)]].map(([l, v]) => (
            <div key={l} className="bg-[#F6F4EF] rounded-2xl p-3">
              <p className="text-xs text-gray-500">{l}</p>
              <p className="font-display font-bold tabular-nums text-sm">{v}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="kcard p-5" data-testid="details-expense-breakdown">
        <h3 className="font-display font-bold mb-3">Dépenses par catégorie</h3>
        {finances.expense_breakdown.length === 0 ? <p className="text-sm text-gray-400 py-4">Aucune dépense sur la période.</p> : (
          <div className="space-y-2.5">
            {finances.expense_breakdown.map((x) => (
              <div key={x.category} data-testid="expense-breakdown-row">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate">{x.category}</span>
                  <span className="font-semibold tabular-nums">{fmtGNF(x.amount)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F0EBE1] mt-1">
                  <div className="h-1.5 rounded-full bg-[#B4432F]" style={{ width: `${(x.amount / max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardDetails() {
  const navigate = useNavigate();
  const { estId } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [advice, setAdvice] = useState("");
  const [loadingAdvice, setLoadingAdvice] = useState(true);

  const load = useCallback(() => {
    const q = `period=${period}${estId ? `&establishment_id=${estId}` : ""}`;
    api.get(`/dashboard/details?${q}`).then((r) => setData(r.data)).catch((e) => logError(e));
    setLoadingAdvice(true);
    api.get(`/dashboard/advice?${q}`).then((r) => setAdvice(r.data.advice)).catch(() => setAdvice(""))
      .finally(() => setLoadingAdvice(false));
  }, [period, estId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-gray-400 py-10">Chargement...</p>;

  return (
    <div className="space-y-5 fade-in" data-testid="dashboard-details-page">
      <button data-testid="details-back-btn" onClick={() => navigate("/accueil")} className="flex items-center gap-1 text-sm text-gray-500">
        <ArrowLeft size={16} /> Retour au dashboard
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-bold text-2xl">Vue détaillée</h1>
        <div className="flex gap-1.5">
          {PERIODS.map(([v, l]) => (
            <button key={v} data-testid={`details-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${period === v ? "bg-[#16233D] text-white font-semibold" : "bg-white border border-[#E9E4DA] text-gray-600"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="kcard p-5" data-testid="details-ai-advice">
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Sparkle size={18} weight="fill" color="#C9793A" /> Conseils de l'assistant</h2>
        {loadingAdvice ? <p className="text-sm text-gray-400">Analyse en cours...</p>
          : <p className="text-sm whitespace-pre-wrap">{(advice || "Conseils indisponibles pour le moment.").replaceAll("**", "")}</p>}
      </div>

      <SectionTitle icon={TrendUp} testid="section-sales">Ventes</SectionTitle>
      <div className="kcard p-5" data-testid="details-combo-chart">
        <h3 className="font-display font-bold mb-1">Évolution : chiffre d'affaires, bénéfice et dépenses</h3>
        <p className="text-xs text-gray-500 mb-3">Les trois courbes superposées montrent l'effet des dépenses sur le bénéfice.</p>
        <ComboChart series={data.combo_series} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="kcard p-5" data-testid="details-top-products">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Package size={18} /> Top produits vendus</h3>
          {data.top_products.length === 0 ? <p className="text-sm text-gray-400 py-6">Aucune vente détaillée.</p> : (
            <div className="space-y-1.5">
              {data.top_products.map((p, i) => (
                <div key={p.name} data-testid="top-product-row" className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-sm">
                  <span className="font-display font-bold text-gray-400 w-5">{i + 1}</span>
                  <span className="flex-1 truncate font-semibold">{p.name}</span>
                  <span className="text-xs text-gray-500 tabular-nums">{p.qty.toLocaleString("fr-FR")} vendus</span>
                  <span className="font-semibold tabular-nums">{fmtGNF(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="kcard p-5" data-testid="details-top-clients">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Users size={18} /> Top clients</h3>
          {data.top_clients.length === 0 ? <p className="text-sm text-gray-400 py-6">Aucun client identifié sur la période.</p> : (
            <div className="space-y-1.5">
              {data.top_clients.map((c, i) => (
                <div key={c.id} data-testid="top-client-row" className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-sm flex-wrap">
                  <span className="font-display font-bold text-gray-400 w-5">{i + 1}</span>
                  <span className="flex-1 min-w-0 truncate font-semibold">{c.name}</span>
                  <span className="font-semibold tabular-nums">{fmtGNF(c.amount)}</span>
                  {c.outstanding > 0
                    ? <span className="badge-unpaid px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums">Doit {fmtGNF(c.outstanding)}</span>
                    : <span className="badge-paid px-2 py-0.5 rounded-full text-xs font-semibold">À jour</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <SectionTitle icon={Package} testid="section-stock">Stock</SectionTitle>
      <StockSection stock={data.stock} />

      <SectionTitle icon={Wallet} testid="section-finances">Finances</SectionTitle>
      <FinanceSection finances={data.finances} />
    </div>
  );
}
